import { parseTagSmEntries, SelectedTagsByCategory, TagSelector } from "@platformx/tag-selector";
import { ShowToastError } from "@platformx/utilities";
import { memo, useRef } from "react";
import { useTranslation } from "react-i18next";

interface Category {
  category: string;
  tags: string[];
}

interface TagSelectorPanelProps {
  subCategory?: string;
  isEdit?: boolean;
  tagData?: Category[];
  selectedTag?: string[];
  selectedTagSm?: unknown;
  handleTagOnChange?: (
    e: { target: { value: string; checked: boolean; tagValue?: string } },
    category?: string,
  ) => void;
  isPublishModal?: boolean;
  isContentReady?: boolean;
}

const MAX_SELECTED_TAGS = 15;

const HIDDEN_TAG_CATEGORIES = ["Content Type", "Collection"];

const buildDefaultSelected = (
  tagData: Category[],
  selectedTag: string[],
  isEdit: boolean,
  subCategory: string,
  selectedTagSm: unknown,
): SelectedTagsByCategory => {
  const result: SelectedTagsByCategory = {};
  const coveredNames = new Set<string>();

  parseTagSmEntries(selectedTagSm).forEach((entry) => {
    if (!selectedTag.includes(entry.name)) return;
    coveredNames.add(entry.name);
    const existing = result[entry.category] || [];
    if (existing.some((tag) => tag.value === entry.value)) return;
    result[entry.category] = [...existing, { name: entry.name, value: entry.value }];
  });

  const remainingTags = selectedTag.filter((tag) => !coveredNames.has(tag));
  if (remainingTags.length > 0) {
    tagData.forEach((categoryGroup) => {
      const matched = (categoryGroup?.tags || []).filter((tag) => remainingTags.includes(tag));
      if (categoryGroup?.category && matched.length > 0) {
        const existing = result[categoryGroup.category] || [];
        result[categoryGroup.category] = [
          ...existing,
          ...matched.map((tag) => ({ name: tag, value: tag })),
        ];
      }
    });
  }

  // Brand-new content: pre-check this content type's own tag, mirroring the old XTags'
  // contentTypeCreateTagHandle.
  if (!isEdit && subCategory && !result["Content Type"]) {
    result["Content Type"] = [{ name: subCategory, value: subCategory }];
  }
  return result;
};

type ReadyProps = {
  tagData: Category[];
  selectedTag: string[];
  isEdit: boolean;
  subCategory: string;
  selectedTagSm: unknown;
  isContentReady: boolean;
  handleTagOnChange?: TagSelectorPanelProps["handleTagOnChange"];
};

const TagSelectorPanelReady = ({
  tagData,
  selectedTag,
  isEdit,
  subCategory,
  selectedTagSm,
  isContentReady,
  handleTagOnChange,
}: ReadyProps) => {
  const { t } = useTranslation();

  const hasSettled =
    isContentReady &&
    (selectedTag.length > 0 || parseTagSmEntries(selectedTagSm).length > 0 || tagData.length > 0);
  const remountKey = useRef<"loading" | "settled">(hasSettled ? "settled" : "loading");

  const initialSelected = buildDefaultSelected(
    tagData,
    selectedTag,
    isEdit,
    subCategory,
    selectedTagSm,
  );

  const previousSelectedRef = useRef<SelectedTagsByCategory>(initialSelected);
  if (hasSettled && remountKey.current !== "settled") {
    remountKey.current = "settled";
    previousSelectedRef.current = initialSelected;
  }

  const handleChange = (selected: SelectedTagsByCategory) => {
    const previous = previousSelectedRef.current;
    const categories = new Set([...Object.keys(previous), ...Object.keys(selected)]);
    for (const category of Array.from(categories)) {
      const prevTags = previous[category] ?? [];
      const nextTags = selected[category] ?? [];
      const added = nextTags.find((tag) => !prevTags.some((p) => p.value === tag.value));
      const removed = prevTags.find((tag) => !nextTags.some((n) => n.value === tag.value));
      if (added) {
        const totalCount = Object.values(selected).reduce((acc, tags) => acc + tags.length, 0);
        if (totalCount > MAX_SELECTED_TAGS) {
          ShowToastError(t("allowed_tags_toast"));
          return;
        }
        previousSelectedRef.current = selected;
        handleTagOnChange?.(
          { target: { value: added.name, checked: true, tagValue: added.value } },
          category,
        );
        return;
      }
      if (removed) {
        previousSelectedRef.current = selected;
        handleTagOnChange?.(
          { target: { value: removed.name, checked: false, tagValue: removed.value } },
          category,
        );
        return;
      }
    }
  };

  return (
    <TagSelector
      key={remountKey.current}
      defaultSelected={initialSelected}
      onChange={handleChange}
      hiddenCategories={HIDDEN_TAG_CATEGORIES}
    />
  );
};

export const TagSelectorPanel = (props: TagSelectorPanelProps) => {
  const { isEdit = false, subCategory = "", isContentReady = true, handleTagOnChange } = props;
  const tagData = Array.isArray(props.tagData) ? props.tagData : [];
  const selectedTag = Array.isArray(props.selectedTag) ? props.selectedTag : [];

  return (
    <TagSelectorPanelReady
      tagData={tagData}
      selectedTag={selectedTag}
      isEdit={isEdit}
      subCategory={subCategory}
      selectedTagSm={props.selectedTagSm}
      isContentReady={isContentReady}
      handleTagOnChange={handleTagOnChange}
    />
  );
};

export default memo(TagSelectorPanel);
